import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { useStreetBeatChat } from '@dev-streetbeat/chat';

import PortfolioConfigData from "config/portfolio-config.json";
import { AssistantService } from "shared/services/AssistantService";
import { CustomDataArtifactRecord } from "types/AssistantTypes";
import { ef_data } from "../FinecoConfigData";
import DistribuzioneTipologiaContainer from "../components/DistribuzioneTipologiaContainer";
import FinecoPortfolioTitle from "../components/FinecoPortfolioTitle";
import { Section } from "../components/Section";
import AnalisiRischioRendimentoAttesoContainer from "../components/analisi-rischio-e-rendimento-atteso/AnalisiRischioRendimentoAttesoContainer";
import AnalisiStrumentiContainer from "../components/analisi-strumenti/AnalisiStrumentiContainer";
import AssetAllocationImplicitaContainer from "../components/asset-allocation-implicita/AssetAllocationImplicitaContainer";
import BacktestContainer from "../components/backtest/BacktestContainer";
import CaratteristicheDiPortafoglioContainer from "../components/caratteristiche-di-portafoglio/CaratteristicheDiPortafoglioContainer";
import PortfolioCommentoCompare from "../components/commento/PortfolioCommentoCompare";
import ComposizioneDelPortafoglioContainer from "../components/composizione-del-portafoglio/ComposizioneDelPortafoglioContainer";
import DistribuzioneDeiProventiContainer from "../components/distribuzione-proventi/DistribuzioneDeiProventiContainer";
import EfficientFrontierCompareContainer from "../components/frontiera-efficiente/EfficientFrontierCompareContainer";
import IndicatoriFinanziariContainer from "../components/indicatori-finanziari/IndicatoriFinanziariContainer";
import ScomposizionePortafolioContainer from "../components/scomposizione-di-portafoglio/ScomposizionePortafolioContainer";
import DistribuzioneEsgContainer from "../components/sostenibilita/components/DistribuzioneEsgContainer";
import useThreadsStore from "shared/store/threadsStore";
import { FinecoPortfolioCompareButtons } from "./components/FinecoPortfolioCompareButtons";
import { getPortfolioData, isFinecoPortfolioArtifact } from "utils/portfolioUtils";

import styles from "pages/assistant/components/artifacts/components/fineco-portfolio/finecoPortfolio.common.module.scss";

type Props = {
  data: CustomDataArtifactRecord;
  artifact: any;
};

export const FinecoPortfolioCompare: React.FC<Props> = memo(
  ({ data, artifact }) => {
    const { t } = useTranslation();
    const params = useParams();
    const threadId = params.threadId;

    const cardRef = useRef<HTMLDivElement>(null);
    const stories = useThreadsStore((state) => state.stories);
    const { messages: chatMessages } = useStreetBeatChat();

    // Extract portfolio IDs directly from the comparison artifact data
    const { portfolioId1, portfolioId2 } = useMemo(() => {
      return {
        portfolioId1: data?.portfolio_1,
        portfolioId2: data?.portfolio_2,
      };
    }, [data]);

    const [reportData, setReportData] = useState<any>(null);
    const [reportData2, setReportData2] = useState<any>(null);
    const [artifactData, setArtifactData] = useState<any>(null);
    const [artifactData2, setArtifactData2] = useState<any>(null);

    useEffect(() => {
      fetchData();
    }, []);
    const fetchData = async () => {
      try {
        // Fetch data using portfolio IDs from the comparison artifact
        if (portfolioId1 && portfolioId2) {
          const [data1, data2] = await Promise.all([
            AssistantService.fetchReportData(portfolioId1),
            AssistantService.fetchReportData(portfolioId2),
          ]);

          setArtifactData(data1.data);
          setArtifactData2(data2.data);

          setReportData(data1);
          setReportData2(data2);
        } else {
          console.warn("No portfolio IDs available for comparison");
        }
      } catch (error) {
        console.error("Error fetching comparison report data:", error);
      }
    };

    const sectionsConfig = useMemo(
      () => ({
        efficientFrontierContainer: {
          label: "",
          component: (
            <EfficientFrontierCompareContainer
              ef_data={ef_data}
              data1={reportData}
              data2={reportData2}
            />
          ),
          isOpen: true,
          showFoldingTitle: false,
        },
        statsContainer: {
          label: "",
          component: (
            <CaratteristicheDiPortafoglioContainer
              data={artifactData}
              data2={artifactData2}
              label={PortfolioConfigData.stats.label}
            />
          ),
          isOpen: true,
          showFoldingTitle: false,
        },
        indicatoriFinanziariContainer: {
          label: "",
          component: (
            <IndicatoriFinanziariContainer
              reportData={reportData}
              reportData2={reportData2}
              label={PortfolioConfigData.indicatori_finanziari.label}
            />
          ),
          isOpen: true,
          showFoldingTitle: false,
        },
        distribuzioneTipologiaStrumentiContainer: {
          label: "",
          component: (
            <DistribuzioneTipologiaContainer
              reportData={reportData}
              reportData2={reportData2}
            />
          ),
          isOpen: true,
          showFoldingTitle: false,
        },
        portfolio: {
          label: PortfolioConfigData.portfolio.label,
          component: (
            <ComposizioneDelPortafoglioContainer
              reportData={reportData}
              reportData2={reportData2}
            />
          ),
          isOpen: true,
          showFoldingTitle: false,
        },
        caratteristicaDiPortafoglio: {
          label: PortfolioConfigData.caratteristiche_di_portafoglio.label,
          component: (
            <DistribuzioneDeiProventiContainer
              reportData={reportData}
              reportData2={reportData2}
            />
          ),
          isOpen: false,
          showFoldingTitle: true,
        },
        assetAllocationImplicita: {
          label: PortfolioConfigData.implicit_asset_allocation.label,
          component: (
            <AssetAllocationImplicitaContainer
              reportData={reportData}
              reportData2={reportData2}
            />
          ),
          isOpen: false,
          showFoldingTitle: true,
        },
        scomposizionePortafoglio: {
          label: PortfolioConfigData.scomposizione_portafoglio.label,
          component: (
            <ScomposizionePortafolioContainer
              reportData={reportData}
              reportData2={reportData2}
            />
          ),
          isOpen: false,
          showFoldingTitle: true,
        },
        analisi_rischio_rendimento_atteso: {
          label: PortfolioConfigData.analisi_rischio_rendimento_atteso.label,
          component: (
            <AnalisiRischioRendimentoAttesoContainer
              reportData={reportData}
              reportData2={reportData2}
            />
          ),
          isOpen: false,
          showFoldingTitle: true,
        },
        backtestContainer: {
          label: PortfolioConfigData.backtest.label,
          component: (
            <BacktestContainer
              reportData={reportData}
              reportData2={reportData2}
            />
          ),
          isOpen: false,
          showFoldingTitle: true,
        },
        analisi_strumenti: {
          label: PortfolioConfigData.analisi_strumenti.label,
          component: (
            <AnalisiStrumentiContainer
              reportData={reportData}
              reportData2={reportData2}
            />
          ),
          isOpen: false,
          showFoldingTitle: true,
        },
        distribuzioneEsg: {
          label: PortfolioConfigData.sostenibilita.label,
          component: (
            <DistribuzioneEsgContainer
              reportData={reportData}
              reportData2={reportData2}
            />
          ),
          isOpen: false,
          showFoldingTitle: true,
        },
        portfolioCommento: {
          label: "Commento al portafoglio",
          component: (
            <PortfolioCommentoCompare
              threadId={threadId}
              portfolio1Id={data.portfolio_1}
              portfolio2Id={data.portfolio_2}
            />
          ),
          isOpen: false,
          showFoldingTitle: true,
        },
      }),

      [reportData, artifact, ef_data, stories],
    );
    const [openSections, setOpenSections] = useState(() => {
      return Object.fromEntries(
        Object.entries(sectionsConfig).map(([key, { isOpen }]) => [
          key,
          isOpen,
        ]),
      );
    });

    const toggleSection = useCallback((section: string) => {
      setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
    }, []);

    //** return jsx */
    if (!reportData) {
      return (
        <div className={styles.loadingPortfolio}>
          {t("portafoglioCompare.loadingPortfolioCompare")}
        </div>
      );
    }

    return (
      <div className={styles.portfolioMainContainer} ref={cardRef}>
        <div className={styles.portfolioContainer}>
          <div className={styles.portfolioContainerInner}>
            <div className={styles.portfolioContainerInnerInner}>
              <FinecoPortfolioTitle
                title={t("portafoglioCompare.PortfolioTitle")}
              />

              {Object.entries(sectionsConfig).map(
                ([sectionKey, { label, component, showFoldingTitle }]) => {
                  if (component.props.children) {
                    const shouldRender = React.Children.toArray(
                      component.props.children,
                    ).some((child) => child !== null);
                    if (!shouldRender) return null;
                  }
                  return (
                    <React.Fragment key={sectionKey}>
                      {(sectionKey === "assetAllocationImplicita" ||
                        sectionKey === "indicatoriFinanziariContainer" ||
                        sectionKey === "caratteristicaDiPortafoglio" ||
                        sectionKey === "analisi_rischio_rendimento_atteso" ||
                        sectionKey === "distribuzioneEsg" ||
                        sectionKey === "portfolioCommento" ||
                        sectionKey === "analisi_strumenti" ||
                        sectionKey === "scomposizionePortafoglio") && (
                          <div className="force-page-break"></div>
                        )}
                      <div className={styles.sectionContainer}>
                        <Section
                          showFoldingTitle={showFoldingTitle}
                          toggleSection={toggleSection}
                          openSections={openSections}
                          reportData={reportData}
                          label={label}
                          section={sectionKey}
                        >
                          <div className={styles.sectionComponentContainer}>
                            {component}
                          </div>
                        </Section>
                      </div>
                    </React.Fragment>
                  );
                },
              )}
            </div>
            <div
              className="print-button-container"
              style={{
                marginTop: "30px",
                display: "flex",
                gap: "10px",
                flexWrap: "wrap",
                marginLeft: "auto",
                justifyContent: "flex-end",
              }}
            >
              <FinecoPortfolioCompareButtons
                threadId={threadId}
                portfolioId1={portfolioId1}
                portfolioId2={portfolioId2}
              />
            </div>
          </div>
        </div>
        <div
          id={`lastAdvisorMessageFinecoPortfolio${artifact.portfolio_id}`}
          className={styles.bottom}
        />
      </div>
    );
  },
);
